// src/pages/Notifications.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
  Bell, ArrowLeft, Trash2, Megaphone, ArrowRight, Mail,
  Check, AlertCircle, Inbox, Lock
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchNotifications = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const q = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Filter notifications for this user
      const myNotifs = all.filter(n =>
        n.recipients?.includes(user.uid)
      );

      setNotifications(myNotifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  const handleDelete = async (notifId) => {
    if (!window.confirm('Delete this notification from your inbox?')) return;
    try {
      // Mark as read by removing user from recipients OR just hide locally
      const seenKey = `notif_deleted_${notifId}_${user.uid}`;
      localStorage.setItem(seenKey, 'true');
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      setSuccessMessage('Notification removed from your inbox.');
      setShowSuccess(true);
    } catch (error) {
      console.error(error);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      const now = new Date();
      const diff = Math.floor((now - d) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} hour(s) ago`;
      if (diff < 604800) return `${Math.floor(diff / 86400)} day(s) ago`;
      return d.toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return ''; }
  };

  // Filter out locally deleted notifications
  const visibleNotifications = notifications.filter(n => {
    if (!user) return true;
    const deletedKey = `notif_deleted_${n.id}_${user.uid}`;
    return !localStorage.getItem(deletedKey);
  });

  if (!user) {
    return (
      <div className="min-h-screen py-12 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="rounded-2xl p-12 anim-scaleIn"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
            <Lock size={64} className="mx-auto mb-4" style={{ color: 'var(--color-secondary-text)' }} />
            <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--color-primary-text)' }}>
              Login Required
            </h2>
            <p className="mb-6 text-sm" style={{ color: 'var(--color-secondary-text)' }}>
              Notifications dekhne ke liye login karein.
            </p>
            <Link to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold btn-2d shadow-lg"
              style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
              Login Now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
      <div className="max-w-4xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)}
            className="p-2 rounded-full btn-2d"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2" style={{ color: 'var(--color-primary-text)' }}>
              <Bell className="text-green-700" size={26} /> Notifications
            </h1>
            <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
              {visibleNotifications.length} {visibleNotifications.length === 1 ? 'notification' : 'notifications'}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
              style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
          </div>
        ) : visibleNotifications.length === 0 ? (
          <div className="text-center py-20">
            <div className="rounded-2xl p-12 anim-scaleIn"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
              <Inbox size={64} className="mx-auto mb-4 anim-float" style={{ color: 'var(--color-secondary-text)' }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
                No Notifications
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                Abhi tak koi notification nahi aayi.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {visibleNotifications.map((notif, i) => (
              <div
                key={notif.id}
                className="rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition card-lift anim-fadeInUp"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  border: `1px solid var(--color-border)`,
                  animationDelay: `${i * 60}ms`,
                }}
              >
                <div
                  className="flex gap-4 p-4 cursor-pointer"
                  onClick={() => setSelected(notif)}
                >
                  {notif.image ? (
                    <img src={notif.image} alt="" className="w-20 h-20 md:w-24 md:h-24 rounded-xl object-cover shrink-0" />
                  ) : (
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)' }}>
                      <Megaphone size={32} style={{ color: '#16a34a' }} />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-base md:text-lg line-clamp-1" style={{ color: 'var(--color-primary-text)' }}>
                        {notif.title}
                      </h3>
                      <span className="w-2 h-2 rounded-full shrink-0 mt-2" style={{ backgroundColor: 'var(--color-badge)' }}></span>
                    </div>
                    <p className="text-sm line-clamp-2 mt-1" style={{ color: 'var(--color-secondary-text)' }}>
                      {notif.description}
                    </p>
                    <p className="text-xs mt-2" style={{ color: 'var(--color-secondary-text)' }}>
                      {formatDate(notif.createdAt)}
                    </p>
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(notif.id); }}
                    className="self-start p-2 rounded-lg hover:bg-red-50 text-red-500 transition shrink-0"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {notif.buttonText && notif.buttonLink && (
                  <div className="px-4 pb-4">
                    <Link
                      to={notif.buttonLink}
                      className="inline-flex items-center gap-1 text-sm font-semibold hover:underline"
                      style={{ color: 'var(--color-link)' }}
                    >
                      {notif.buttonText} <ArrowRight size={14} />
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Detail Modal */}
      {selected && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 anim-fadeIn"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSelected(null)}
        >
          <div
            className="rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto anim-popIn"
            style={{ backgroundColor: 'var(--color-card-bg)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {selected.image && (
              <img src={selected.image} alt="" className="w-full h-56 object-cover rounded-t-3xl" />
            )}

            <div className="p-6">
              <div className="flex justify-between items-start mb-3">
                <h2 className="text-xl font-bold flex-1" style={{ color: 'var(--color-primary-text)' }}>
                  {selected.title}
                </h2>
              </div>

              <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--color-secondary-text)' }}>
                {selected.description}
              </p>

              {selected.buttonText && selected.buttonLink && (
                <Link
                  to={selected.buttonLink}
                  onClick={() => setSelected(null)}
                  className="w-full rounded-full py-3 font-bold flex items-center justify-center gap-2 btn-2d shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, #16a34a 0%, #14532d 100%)',
                    color: '#ffffff',
                  }}
                >
                  {selected.buttonText} <ArrowRight size={16} />
                </Link>
              )}

              <p className="text-xs text-center mt-4" style={{ color: 'var(--color-secondary-text)' }}>
                {formatDate(selected.createdAt)}
              </p>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Done!"
        message={successMessage}
        buttonText="OK"
      />

    </div>
  );
};

export default Notifications;